import { BasicButton, Link, PrimaryButton } from '../../../../widgets/buttons';
import { Checkbox } from '../../../../widgets/checkbox/checkbox';
import { Dialog, DialogTitle, DialogWindow } from '../../../../widgets/dialog';
import { FormControl } from '../../../../widgets/formControl';
import { Input } from '../../../../widgets/input';
import { NotificationBanner } from '../../../../widgets/notificationBanner/notificationBanner';
import { removeNotification, showErrorNotification, showInfoNotification, showNotification } from '../../../../widgets/notificationToaster';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useMutation } from 'react-relay';
import { backofficeUrl, T } from 'src/shared/helpers';
import styled from 'styled-components';
import { providerQueryAddMutation } from '../__generated__/providerQueryAddMutation.graphql';
import { ProductProviders } from '../providerElements';
import { addQuery } from '../providerQuery';
import { clearWizardProgress, loadWizardProgress, saveWizardProgress } from '../wizardProgress';

const PRINTEERS_GUIDE_URL = 'https://zakeke.zendesk.com/hc/en-us/articles/360010238519-How-to-connect-and-use-Printeers-with-Zakeke';

const StyledDialogWindow = styled(DialogWindow)`
    max-width: 700px;
    height: calc(90vh - 20px);
    padding-left: 24px !important;
    padding-right: 24px !important;
    display: flex;
    flex-direction: column;

    > div {
        overflow: visible;
        display: flex;
        flex-direction: column;
        flex: 1;
        min-height: 0;
    }
`;

const DialogCaption = styled.p`
    font-size: 13px;
    line-height: 18px;
    color: #6b6b6b;
    margin: -16px 0 20px;
`;

const WizardContainer = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
`;

const StepIndicator = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin-bottom: 32px;
`;

const StepDot = styled.div<{ $active: boolean; $completed: boolean; $clickable: boolean }>`
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    font-weight: 600;
    color: ${(props) => (props.$active || props.$completed ? '#fff' : '#9e9e9e')};
    background: ${(props) => (props.$completed ? '#2e7d32' : props.$active ? props.theme.app.primary : '#e0e0e0')};
    transition: all 0.2s ease;
    cursor: ${(props) => (props.$clickable ? 'pointer' : 'default')};

    &:hover {
        opacity: ${(props) => (props.$clickable ? 0.8 : 1)};
    }
`;

const StepLine = styled.div<{ $completed: boolean }>`
    width: 40px;
    height: 2px;
    background: ${(props) => (props.$completed ? '#2e7d32' : '#e0e0e0')};
    transition: all 0.2s ease;
`;

const StepContentArea = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-height: 0;
`;

const StepTitle = styled.h3`
    font-size: 16px;
    font-weight: 600;
    margin: 0 0 8px;
`;

const StepBody = styled.div`
    font-size: 13px;
    line-height: 18px;
    color: #333;
    display: flex;
    flex-direction: column;
    gap: 8px;

    p {
        margin: 4px 0;
    }

    ul {
        margin: 4px 0;
    }

    ${NotificationBanner} {
        margin: 4px 0;
    }
`;

const ButtonContainer = styled.div`
    display: flex;
    justify-content: flex-start;
    gap: 8px;
    padding-top: 8px;
`;

const IntegrateContainer = styled.div`
    padding: 32px;
    display: flex;
    justify-content: center;
`;

const GuideLinkSpacer = styled.div`
    flex: 1;
    min-height: 20px;
`;

const GuideLink = styled.div`
    padding: 12px 0 0;
    border-top: 1px solid #dbe2e6;
`;

const SuccessMessage = styled.div`
    font-size: 14px;
    font-weight: 600;
    color: #2e7d32;
    text-align: center;
    padding: 12px;
`;

const StepIndicatorBar: FC<{
    totalSteps: number;
    currentStep: number;
    completedSteps: boolean[];
    onStepClick?: (step: number) => void;
}> = ({ totalSteps, currentStep, completedSteps, onStepClick }) => {
    return (
        <StepIndicator>
            {Array.from({ length: totalSteps }, (_, i) => {
                const canClick = completedSteps[i] && i < currentStep;
                return (
                    <React.Fragment key={i}>
                        {i > 0 && <StepLine $completed={completedSteps[i - 1]} />}
                        <StepDot $active={currentStep === i} $completed={completedSteps[i]} $clickable={canClick} onClick={() => canClick && onStepClick?.(i)}>
                            {completedSteps[i] ? '✓' : i + 1}
                        </StepDot>
                    </React.Fragment>
                );
            })}
        </StepIndicator>
    );
};

interface PrinteersIntegrationWizardProps {
    onAuthorized: () => void;
}

const PrinteersIntegrationWizard: FC<PrinteersIntegrationWizardProps> = ({ onAuthorized }) => {
    const savedProgress = useRef(loadWizardProgress(ProductProviders.Invition));
    const initialStep1Confirmed = savedProgress.current?.confirmations[0] ?? false;
    const initialStep = Math.min(savedProgress.current?.step ?? 0, initialStep1Confirmed ? 1 : 0);
    const [currentStep, setCurrentStep] = useState(initialStep);
    const [step1Confirmed, setStep1Confirmed] = useState(initialStep1Confirmed);
    const [apikey, setApikey] = useState('');
    const [partnerCode, setPartnerCode] = useState('');
    const [step2Confirmed, setStep2Confirmed] = useState(false);
    const [phoneNumber, setPhoneNumber] = useState('');
    const [saved, setSaved] = useState(false);
    const [addApiKey] = useMutation<providerQueryAddMutation>(addQuery);

    useEffect(() => {
        if (saved) return;
        saveWizardProgress(ProductProviders.Invition, currentStep, [step1Confirmed, step2Confirmed, saved]);
    }, [currentStep, step1Confirmed, step2Confirmed, saved]);

    const handleStep1Toggle = (checked: boolean) => {
        if (checked) {
            setStep1Confirmed(true);
            setCurrentStep(1);
        } else {
            setStep1Confirmed(false);
            setStep2Confirmed(false);
            setCurrentStep(0);
        }
    };

    const handleStep2Confirm = () => {
        if (!apikey.trim() || !partnerCode.trim()) {
            showErrorNotification({ message: T._('Please fill in both fields before continuing', 'Admin'), dismiss: { duration: 5000 } });
            return;
        }
        setStep2Confirmed(true);
        setCurrentStep(2);
    };

    const handleStepClick = (step: number) => {
        if (step === 0) {
            setStep1Confirmed(false);
            setStep2Confirmed(false);
        } else if (step === 1) {
            setStep2Confirmed(false);
        }
        setCurrentStep(step);
    };

    const handleSave = () => {
        if (!phoneNumber || phoneNumber.trim() === '') {
            showErrorNotification({ message: T._('The phone number is mandatory', 'Admin'), dismiss: { duration: 5000 } });
            return;
        }

        const notification = showInfoNotification({
            message: T._(`Creating Api Key... please don't exit from page.`, 'Admin'),
            dismiss: undefined
        });
        addApiKey({
            variables: {
                input: {
                    providerId: ProductProviders.Invition,
                    secretKey: JSON.stringify({ Apikey: apikey, Partnercode: partnerCode }),
                    descriptionKey: 'Invition key',
                    autoConfirmOrders: false,
                    printfulPrefix: null,
                    printfulSuffix: null,
                    invitionPhoneNumber: phoneNumber
                }
            },
            onCompleted: () => {
                removeNotification(notification);
                showNotification({ type: 'success', message: T._('Api Key created.', 'Admin'), dismiss: { duration: 5000 } });
                setSaved(true);
                clearWizardProgress(ProductProviders.Invition);
                onAuthorized();
                window.location.href = backofficeUrl('/integration/provider-configuration-page/printeers');
            },
            onError: () => {
                removeNotification(notification);
                showNotification({ type: 'danger', message: T._('Error on creating', 'Admin'), dismiss: { duration: 5000 } });
            },
            updater: (store) => {
                const root = store.getRoot();
                const addedPOD = store.getRootField('addApiKey');
                const oldPOD = (root.getLinkedRecords('apiKey', {}) as []) ?? [];
                const allPOD = [...oldPOD, addedPOD];
                root.setLinkedRecords(allPOD, 'apiKey', {});
            }
        });
    };

    const completedSteps = [step1Confirmed, step2Confirmed, saved];

    return (
        <WizardContainer>
            <NotificationBanner
                type={'INFO'}
                variant={'slim'}
                content={
                    <>
                        {T._('We recommend following along with the', 'Admin')}{' '}
                        <Link target='_blank' href={PRINTEERS_GUIDE_URL}>
                            {T._('full integration guide', 'Admin')}
                        </Link>
                        {T._(', which includes screenshots for each step.', 'Admin')}
                    </>
                }
            />

            <StepIndicatorBar totalSteps={3} currentStep={currentStep} completedSteps={completedSteps} onStepClick={handleStepClick} />

            <StepContentArea>
                {currentStep === 0 && (
                    <StepBody>
                        <StepTitle>{T._('Create an account with Printeers', 'Admin')}</StepTitle>
                        <p>
                            <Link target='_blank' href={'https://dashboard.printeers.com/signup'}>
                                {T._('Sign up on the Printeers website', 'Admin')}
                            </Link>
                            {T._('. After registration, Printeers will send you a Partner Code and an API key.', 'Admin')}
                        </p>
                        <NotificationBanner
                            type={'WARNING'}
                            variant={'slim'}
                            content={T._('Make sure you have received both your Partner Code and API key from Printeers before continuing.', 'Admin')}
                        />
                        <Checkbox
                            checked={step1Confirmed}
                            onChange={handleStep1Toggle}
                            label={T._('I have received my Partner Code and API key from Printeers', 'Admin')}
                        />
                    </StepBody>
                )}

                {currentStep === 1 && (
                    <StepBody>
                        <StepTitle>{T._('Enter your API key and Partner Code', 'Admin')}</StepTitle>
                        <p>{T._('Enter the API key and Partner Code you received from Printeers.', 'Admin')}</p>
                        <FormControl label={T._('Api Key', 'Admin')}>
                            <Input
                                type='string'
                                value={apikey}
                                onChange={(e) => setApikey(e.currentTarget.value)}
                                placeholder={T._('Insert your Api key...', 'Admin')}
                            />
                        </FormControl>
                        <FormControl label={T._('Partner Code', 'Admin')}>
                            <Input
                                type='string'
                                value={partnerCode}
                                onChange={(e) => setPartnerCode(e.currentTarget.value)}
                                placeholder={T._('Insert your Partner Code...', 'Admin')}
                            />
                        </FormControl>
                        <ButtonContainer>
                            <PrimaryButton size='small' onClick={handleStep2Confirm} disabled={!apikey.trim() || !partnerCode.trim()}>
                                {T._('Continue', 'Admin')}
                            </PrimaryButton>
                        </ButtonContainer>
                    </StepBody>
                )}

                {currentStep === 2 && (
                    <StepBody>
                        <StepTitle>{T._('Add a default phone number', 'Admin')}</StepTitle>
                        <p>
                            {T._(
                                'Printeers requires a phone number for order processing. Set a default number to use as a fallback when clients do not provide their own.',
                                'Admin'
                            )}
                        </p>
                        {!saved ? (
                            <>
                                <FormControl label={T._('Phone number', 'Admin')}>
                                    <Input type={'tel'} value={phoneNumber} onChange={(e) => setPhoneNumber(e.currentTarget.value)} />
                                </FormControl>
                                <ButtonContainer>
                                    <BasicButton
                                        size='small'
                                        onClick={() => {
                                            setStep2Confirmed(false);
                                            setCurrentStep(1);
                                        }}
                                    >
                                        {T._('Back', 'Admin')}
                                    </BasicButton>
                                    <PrimaryButton size='small' onClick={handleSave} disabled={!phoneNumber.trim()}>
                                        {T._('Save', 'Admin')}
                                    </PrimaryButton>
                                </ButtonContainer>
                            </>
                        ) : (
                            <SuccessMessage>{T._('Printeers integration is complete! You are all set.', 'Admin')}</SuccessMessage>
                        )}
                    </StepBody>
                )}
            </StepContentArea>

            <GuideLinkSpacer />

            <GuideLink>
                <Link target='_blank' href={PRINTEERS_GUIDE_URL}>
                    {T._('Need help? Read the full integration guide', 'Admin')}
                </Link>
            </GuideLink>
        </WizardContainer>
    );
};

export const PrinteersIntegrationDialog: FC<{ onClose: () => void; onIntegrated: () => void }> = ({ onClose, onIntegrated }) => {
    return (
        <Dialog windowDecorator={StyledDialogWindow} onClose={onClose}>
            <DialogTitle>
                {T._('Connect', 'Admin')} Printeers {T._('to your Zakeke account', 'Admin')}
            </DialogTitle>
            <DialogCaption>{T._('Sell Printeers products as customizable on your store with automatic fulfillment and drop-shipping.', 'Admin')}</DialogCaption>
            <PrinteersIntegrationWizard onAuthorized={onIntegrated} />
        </Dialog>
    );
};

export const PrinteersIntegrateButton: FC<{ onIntegrated: () => void }> = ({ onIntegrated }) => {
    const [showDialog, setShowDialog] = useState(false);

    return (
        <>
            <IntegrateContainer>
                <PrimaryButton size='small' onClick={() => setShowDialog(true)}>
                    {T._('Integrate', 'Admin')}
                </PrimaryButton>
            </IntegrateContainer>
            {showDialog && <PrinteersIntegrationDialog onClose={() => setShowDialog(false)} onIntegrated={onIntegrated} />}
        </>
    );
};

export default PrinteersIntegrationWizard;
